<template>
  <Address
    :address
    data-qa="billing-address"
    :edit-button-title="i18n(translations.edit)"
    :show-form="isFormVisible"
    :title="i18n(translations.title)"
    @edit="onEdit"
  >
    <BillingAddressForm
      :address="billingAddress"
      :api-errors
      :countries="formatCountries(billableCountries)"
      :submit-button-label="i18n(translations.submitButton)"
      :submitting
      :with-national-id="false"
      :with-title="false"
      :with-user-information="false"
      @country-change="(country) => emit('country-change', country)"
      @submit="onSubmit"
    />
  </Address>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import type { Bill } from '@backmarket/nuxt-layer-checkout/api/cart/types'
import { useLogUnhandledAPIErrors } from '@backmarket/nuxt-module-address/useLogUnhandledAPIErrors'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'

import useHandleUnauthorizedUser from '~/scopes/checkout/composables/useHandleUnauthorizedUser'
import type { FormValues } from '~/scopes/checkout/flows/e-commerce/pages/BillingAddressStep/components/BillingAddress/components/BillingAddressForm.types'
import BillingAddressForm from '~/scopes/checkout/flows/e-commerce/pages/BillingAddressStep/components/BillingAddress/components/BillingAddressForm.vue'
import { getAPIErrors } from '~/scopes/checkout/utils/getAPIErrors'

import { formatCountries } from '../../utils/formatCountries'
import Address from '../Address/Address.vue'

import translations from './BillingAddress.translations'

const props = defineProps<{
  address: ApiSpecs['/bm/cart']['GET']['response']['addressList']['bill']
  billableCountries: ApiSpecs['/bm/cart']['GET']['response']['billableCountries']
}>()

const emit = defineEmits<{
  (e: 'updating'): void
  (e: 'updated', callback?: () => void): void
  (e: 'country-change', country?: string): void
}>()

const i18n = useI18n()
const tracking = useTracking()
const $httpFetch = useHttpFetch()

const isFormVisible = ref(!props.address)
const submitting = ref(false)
const apiErrors = ref({})

const { openErrorToast } = useTheToast()
const { logUnhandledAPIErrors } = useLogUnhandledAPIErrors()
const { handleUnauthorizedUser } = useHandleUnauthorizedUser()

const defaultAddress: Bill = {
  birthdate: null,
  city: null,
  company: null,
  country: null,
  countryDialInCode: null,
  customerIdNumber: null,
  dateCreation: null,
  dateModification: null,
  email: null,
  firstName: null,
  firstNamePronunciation: null,
  lastName: null,
  lastNamePronunciation: null,
  phone: null,
  postalCode: null,
  stateOrProvince: null,
  street: null,
  street2: null,
}

const billingAddress = computed(() => {
  return props.address ?? defaultAddress
})

watch(
  () => isFormVisible,
  (isVisible) => {
    if (isVisible) {
      tracking.trackBlockImpression({
        name: 'billing_address_form',
      })
    }
  },
  { immediate: true },
)

async function onEdit() {
  tracking.trackClick({
    name: 'edit_billing_address',
    zone: 'address',
  })

  isFormVisible.value = true
}

async function onSubmit(values: Omit<FormValues, 'nationalId'>) {
  emit('updating')
  apiErrors.value = {}
  submitting.value = true

  try {
    await $httpFetch('/bm/client/addresses/v2/billing', {
      method: 'POST',
      body: removeEmptyValuesInObject<Bill>({
        ...billingAddress.value,
        ...values,
      }) as ApiSpecs['/bm/client/addresses/v2/billing']['POST']['body'],
    })

    tracking.trackClick({
      name: 'billing_address_saved',
      zone: 'address',
    })

    emit('updated', () => {
      isFormVisible.value = false
      submitting.value = false
    })
  } catch (e) {
    const error = e as Record<string, unknown>

    tracking.trackClick({
      name: 'billing_address_saved_failure',
      zone: 'address',
    })

    if (error?.status === 400 || error?.status === 422) {
      const errors = getAPIErrors(error)
      logUnhandledAPIErrors(Object.keys(errors))
      apiErrors.value = errors
      openErrorToast()
    } else
      await handleUnauthorizedUser(
        error,
        '[CHECKOUT] Failed to submit billing form',
      )

    submitting.value = false
  }
}

defineExpose({
  isInEditMode: () => isFormVisible.value,
})
</script>
